import userProfilePicture from 'assets/images/users/user-dummy-img.jpg'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Modal, Spinner } from 'reactstrap'
import { inviteCreatorsToCampaign, searchCampaigns } from 'services/api/campaign'
import { CampaignInviteCreatorPreview } from 'slices/ui/reducer'
import { RootState } from 'store'
import eventEmitter from 'utils/eventEmitter'
import { formatNumber, getErrorMessage, textToTitleCase } from 'utils/helpers'

const SEARCH_DEBOUNCE_MS = 400

interface CampaignSearchResult {
    uuid: string
    name: string
    status?: string
    platform?: string
    mode?: string
    hero_img_url?: string | null
    brand?: { img_url?: string | null; name?: string } | null
    budget?: string | number
    remaining_budget?: string | number
    applications_count?: number
    total_application_count?: number
    collaborations_count?: number
    collaboration_count?: number
    active_at?: string | null
    created?: string
    created_at?: string
}

type InviteResultState =
    | { type: 'idle' }
    | { type: 'success'; message: string }
    | { type: 'error'; message: string }

interface CampaignInviteModalProps {
    isOpen: boolean
    onClose: () => void
    socialAccountIds: string[]
    creators?: CampaignInviteCreatorPreview[]
    onInviteSuccess?: () => void
}

const CampaignInviteModal: React.FC<CampaignInviteModalProps> = ({
    isOpen,
    onClose,
    socialAccountIds,
    creators = [],
    onInviteSuccess,
}) => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId)

    const [searchQuery, setSearchQuery] = useState('')
    const [campaigns, setCampaigns] = useState<CampaignSearchResult[]>([])
    const [campaignsLoading, setCampaignsLoading] = useState(false)
    const [selectedCampaign, setSelectedCampaign] = useState<CampaignSearchResult | null>(null)
    const [inviting, setInviting] = useState(false)
    const [result, setResult] = useState<InviteResultState>({ type: 'idle' })

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const requestIdRef = useRef(0)

    const creatorCount = socialAccountIds.length
    const previewCreators: CampaignInviteCreatorPreview[] = useMemo(() => {
        if (creators.length > 0) {
            return creators.filter((c) => socialAccountIds.includes(c.uuid))
        }
        return socialAccountIds.map((uuid) => ({
            uuid,
            name: 'Creator',
            handle: uuid.slice(0, 8),
            profile_picture_url: null,
        }))
    }, [creators, socialAccountIds])

    const resetState = () => {
        setSearchQuery('')
        setCampaigns([])
        setSelectedCampaign(null)
        setInviting(false)
        setResult({ type: 'idle' })
        setCampaignsLoading(false)
    }

    const fetchCampaigns = async (q: string) => {
        if (!selectedAgencyId) return
        const requestId = ++requestIdRef.current
        setCampaignsLoading(true)
        try {
            const data = await searchCampaigns(q, selectedAgencyId, {mode: 'workflow', platform: 'ig'})
            if (requestId !== requestIdRef.current) return
            const list: CampaignSearchResult[] = Array.isArray(data)
                ? data
                : Array.isArray((data as { items?: CampaignSearchResult[] })?.items)
                  ? (data as { items: CampaignSearchResult[] }).items
                  : []
            setCampaigns(list)
        } catch {
            if (requestId !== requestIdRef.current) return
            setCampaigns([])
        } finally {
            if (requestId === requestIdRef.current) {
                setCampaignsLoading(false)
            }
        }
    }

    useEffect(() => {
        if (!isOpen) {
            resetState()
            return
        }
        fetchCampaigns('')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, selectedAgencyId])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const handleSearchChange = (value: string) => {
        setSearchQuery(value)
        setSelectedCampaign(null)
        setResult({ type: 'idle' })
        if (debounceRef.current) clearTimeout(debounceRef.current)
        setCampaignsLoading(true)
        debounceRef.current = setTimeout(() => {
            fetchCampaigns(value.trim())
        }, SEARCH_DEBOUNCE_MS)
    }

    const handleSelectCampaign = (campaign: CampaignSearchResult) => {
        setSelectedCampaign(campaign)
        setResult({ type: 'idle' })
    }

    const handleInvite = async () => {
        if (!selectedAgencyId || !selectedCampaign || socialAccountIds.length === 0 || inviting) return

        setInviting(true)
        setResult({ type: 'idle' })
        try {
            const response = await inviteCreatorsToCampaign(
                selectedAgencyId,
                selectedCampaign.uuid,
                socialAccountIds
            )
            const message =
                response?.message ||
                `Successfully invited ${creatorCount} creator${creatorCount === 1 ? '' : 's'} to ${selectedCampaign.name}`
            setResult({ type: 'success', message })
            onInviteSuccess?.()
            eventEmitter.emit('campaign-invite-success', {
                campaignId: selectedCampaign.uuid,
                socialAccountIds,
            })
        } catch (error) {
            setResult({
                type: 'error',
                message: getErrorMessage(error, 'Failed to invite creators to campaign'),
            })
            toast.error(getErrorMessage(error, 'Failed to invite creators to campaign'))
        } finally {
            setInviting(false)
        }
    }

    const handleClose = () => {
        if (inviting) return
        onClose()
    }

    const applicationCount =
        selectedCampaign?.total_application_count ?? selectedCampaign?.applications_count
    const collaborationCount =
        selectedCampaign?.collaboration_count ?? selectedCampaign?.collaborations_count
    const campaignImage =
        selectedCampaign?.brand?.img_url || selectedCampaign?.hero_img_url || null

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            centered
            size="lg"
            className="campaign-invite-modal-parent"
            contentClassName="campaign-invite-modal-content"
            backdrop={inviting ? 'static' : true}
            keyboard={!inviting}
        >
            <div className="campaign-invite-modal">
                <div className="campaign-invite-modal__header">
                    <div>
                        <h2 className="campaign-invite-modal__title">Invite to Campaign</h2>
                        <p className="campaign-invite-modal__subtitle">
                            Select a campaign to invite {creatorCount} selected creator
                            {creatorCount === 1 ? '' : 's'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="campaign-invite-modal__close"
                        onClick={handleClose}
                        disabled={inviting}
                        aria-label="Close"
                    >
                        <i className="ri-close-line" />
                    </button>
                </div>

                <div className="campaign-invite-modal__body">
                    <section className="campaign-invite-modal__section">
                        <div className="campaign-invite-modal__section-header">
                            <h3 className="campaign-invite-modal__section-title">Creators</h3>
                            <span className="campaign-invite-modal__badge">{creatorCount}</span>
                        </div>
                        <div className="campaign-invite-modal__creators">
                            {previewCreators.map((creator) => (
                                <div key={creator.uuid} className="campaign-invite-modal__creator">
                                    <div className="campaign-invite-modal__creator-avatar-wrap">
                                        <img
                                            src={
                                                creator.profile_picture_url?.split('=')[0] ??
                                                userProfilePicture
                                            }
                                            alt={creator.name}
                                            referrerPolicy="no-referrer"
                                            className="campaign-invite-modal__creator-avatar"
                                        />
                                        {creator.platform === 'ig' && (
                                            <span className="campaign-invite-modal__platform campaign-invite-modal__platform--ig">
                                                <i className="ri-instagram-line" />
                                            </span>
                                        )}
                                        {creator.platform === 'yt' && (
                                            <span className="campaign-invite-modal__platform campaign-invite-modal__platform--yt">
                                                <i className="ri-youtube-fill" />
                                            </span>
                                        )}
                                    </div>
                                    <div className="campaign-invite-modal__creator-info">
                                        <span className="campaign-invite-modal__creator-name">
                                            {creator.name}
                                        </span>
                                        <span className="campaign-invite-modal__creator-handle">
                                            @{creator.handle}
                                            {typeof creator.followers_count === 'number' && (
                                                <> · {formatNumber(creator.followers_count)} followers</>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {result.type === 'success' ? (
                        <section className="campaign-invite-modal__result campaign-invite-modal__result--success">
                            <div className="campaign-invite-modal__result-icon">
                                <i className="ri-checkbox-circle-fill" />
                            </div>
                            <h3 className="campaign-invite-modal__result-title">Invites sent</h3>
                            <p className="campaign-invite-modal__result-message">{result.message}</p>
                            {selectedCampaign && (
                                <div className="campaign-invite-modal__selected campaign-invite-modal__selected--result">
                                    <CampaignThumb campaign={selectedCampaign} image={campaignImage} />
                                    <div>
                                        <span className="campaign-invite-modal__selected-name">
                                            {selectedCampaign.name}
                                        </span>
                                        {selectedCampaign.status && (
                                            <span className="campaign-invite-modal__selected-meta">
                                                {textToTitleCase(selectedCampaign.status)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </section>
                    ) : (
                        <>
                            <section className="campaign-invite-modal__section">
                                <div className="campaign-invite-modal__section-header">
                                    <h3 className="campaign-invite-modal__section-title">Campaign</h3>
                                </div>
                                <div className="campaign-invite-modal__search">
                                    <i className="ri-search-2-line" />
                                    <input
                                        type="text"
                                        className="campaign-invite-modal__search-input"
                                        placeholder="Search campaigns by name..."
                                        value={searchQuery}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        disabled={inviting}
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            className="campaign-invite-modal__search-clear"
                                            onClick={() => handleSearchChange('')}
                                            aria-label="Clear search"
                                        >
                                            <i className="ri-close-line" />
                                        </button>
                                    )}
                                </div>

                                <div className="campaign-invite-modal__campaigns">
                                    {campaignsLoading &&
                                        Array.from({ length: 4 }).map((_, index) => (
                                            <div key={index} className="campaign-invite-modal__campaign-skeleton">
                                                <Skeleton height={48} className="rounded-3" />
                                            </div>
                                        ))}

                                    {!campaignsLoading &&
                                        campaigns.map((campaign) => {
                                            const isSelected = selectedCampaign?.uuid === campaign.uuid
                                            const image =
                                                campaign.brand?.img_url || campaign.hero_img_url || null
                                            return (
                                                <button
                                                    key={campaign.uuid}
                                                    type="button"
                                                    className={`campaign-invite-modal__campaign${
                                                        isSelected
                                                            ? ' campaign-invite-modal__campaign--selected'
                                                            : ''
                                                    }`}
                                                    onClick={() => handleSelectCampaign(campaign)}
                                                    disabled={inviting}
                                                >
                                                    <CampaignThumb campaign={campaign} image={image} />
                                                    <div className="campaign-invite-modal__campaign-info">
                                                        <span className="campaign-invite-modal__campaign-name">
                                                            {campaign.platform === 'ig' && (
                                                                <i className="ri-instagram-line text-danger me-1" />
                                                            )}
                                                            {campaign.platform === 'yt' && (
                                                                <i className="ri-youtube-fill text-danger me-1" />
                                                            )}
                                                            {campaign.name}
                                                        </span>
                                                        <span className="campaign-invite-modal__campaign-meta">
                                                            {campaign.status &&
                                                                textToTitleCase(campaign.status)}
                                                            {campaign.mode === 'offline' && ' · Track Only'}
                                                            {(campaign.active_at ||
                                                                campaign.created ||
                                                                campaign.created_at) &&
                                                                ` · ${
                                                                    campaign.active_at ||
                                                                    campaign.created ||
                                                                    campaign.created_at
                                                                }`}
                                                        </span>
                                                    </div>
                                                    <span
                                                        className={`campaign-invite-modal__radio${
                                                            isSelected
                                                                ? ' campaign-invite-modal__radio--checked'
                                                                : ''
                                                        }`}
                                                    >
                                                        {isSelected && <i className="ri-check-line" />}
                                                    </span>
                                                </button>
                                            )
                                        })}

                                    {!campaignsLoading && campaigns.length === 0 && (
                                        <div className="campaign-invite-modal__empty">
                                            <i className="ri-inbox-line" />
                                            <p>No campaigns found</p>
                                        </div>
                                    )}
                                </div>
                            </section>
                        </>
                    )}
                </div>

                <div className="campaign-invite-modal__footer">
                    {result.type === 'success' ? (
                        <button
                            type="button"
                            className="btn btn-primary campaign-invite-modal__primary-btn"
                            onClick={handleClose}
                        >
                            Done
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="btn btn-outline-secondary campaign-invite-modal__secondary-btn"
                                onClick={handleClose}
                                disabled={inviting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary campaign-invite-modal__primary-btn"
                                onClick={handleInvite}
                                disabled={!selectedCampaign || inviting || creatorCount === 0}
                            >
                                {inviting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Inviting...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-mail-send-line me-1" />
                                        Invite {creatorCount} Creator{creatorCount === 1 ? '' : 's'}
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    )
}

const CampaignThumb: React.FC<{
    campaign: CampaignSearchResult
    image: string | null
    size?: 'sm' | 'lg'
}> = ({ campaign, image, size = 'sm' }) => {
    const initial = (campaign.name || 'C').charAt(0).toUpperCase()
    return (
        <div
            className={`campaign-invite-modal__thumb campaign-invite-modal__thumb--${size}${
                image ? '' : ' campaign-invite-modal__thumb--placeholder'
            }`}
            style={image ? { backgroundImage: `url(${image})` } : undefined}
        >
            {!image && <span>{initial}</span>}
        </div>
    )
}

export default CampaignInviteModal
